import { useState, useEffect } from "react";
import { Sparkles, ArrowRight } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { authFetch } from "@/lib/authFetch";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// FIN-3 — Banner proativo do Nexo.
//
// Diferencial #4 (real-time proativo): agrega sinais de risco e mostra
// 1 alerta priorizado no topo. Se nada crítico, banner não renderiza.
//
// Sinais (ordem de prioridade):
//   1. Cartão com fatura vencendo HOJE ou ATRASADA
//   2. Cartão com fatura vencendo em ≤ 3 dias
//   3. Uso de crédito agregado em estado critical (≥90%)
//   4. Uso de crédito agregado em warning (70-89%)
//
// CTA "Falar com Nexo" carrega prompt contextual.

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

export default function NexoProactiveAlert({ creditCards, creditUsage, onTalk }) {
  // Lazy fetch dos days_to_due de cada cartão pra detectar vencimento iminente.
  const [duesByCard, setDuesByCard] = useState({});

  useEffect(() => {
    if (!creditCards || creditCards.length === 0) return;
    let cancelled = false;
    (async () => {
      const out = {};
      await Promise.all(creditCards
        .filter(c => c.closing_day && c.due_day)
        .map(async (c) => {
          try {
            const r = await authFetch(`${API_URL}/api/accounts/${c.id}/fatura-estimada`);
            if (r.ok) {
              const data = await r.json();
              out[c.id] = { days: data.days_to_due, fatura: data.fatura_atual, name: c.name };
            }
          } catch (_) { /* skip */ }
        }));
      if (!cancelled) setDuesByCard(out);
    })();
    return () => { cancelled = true; };
  }, [creditCards]);

  // Computa alert priorizado.
  let alert = null;
  const dues = Object.values(duesByCard);
  const overdue = dues.filter(d => d.days != null && d.days <= 0);
  const dueSoon = dues.filter(d => d.days != null && d.days > 0 && d.days <= 3);
  const usagePct = creditUsage?.usage_pct || 0;
  const usageLevel = creditUsage?.alert_level || "ok";

  if (overdue.length > 0) {
    const d = overdue[0];
    alert = {
      tone: "critical",
      title: d.days < 0 ? `Fatura ${d.name} atrasada há ${Math.abs(d.days)} dia${Math.abs(d.days) === 1 ? "" : "s"}` : `Fatura ${d.name} vence hoje`,
      subtitle: `${fmtBRL(d.fatura || 0)} pendente. Pagar agora evita juros.`,
      promptHint: `A fatura do ${d.name} está vencendo. O que devo fazer?`,
    };
  } else if (dueSoon.length > 0) {
    const d = dueSoon[0];
    alert = {
      tone: "warning",
      title: `Fatura ${d.name} vence em ${d.days} dia${d.days === 1 ? "" : "s"}`,
      subtitle: `${fmtBRL(d.fatura || 0)} a pagar. Programe o pagamento.`,
      promptHint: `A fatura do ${d.name} vence em ${d.days} dia${d.days === 1 ? "" : "s"}. Tem algo que eu deva saber?`,
    };
  } else if (usageLevel === "critical") {
    alert = {
      tone: "critical",
      title: `Uso de crédito em ${usagePct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% do limite`,
      subtitle: "Você está perto do limite combinado. Considere segurar gastos.",
      promptHint: "Estou em uso crítico de crédito. Como devo organizar os próximos gastos?",
    };
  } else if (usageLevel === "warning") {
    alert = {
      tone: "warning",
      title: `Uso de crédito em ${usagePct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% do limite`,
      subtitle: "Acima de 70%. Bom momento pra revisar gastos previstos.",
      promptHint: "Como está meu uso de cartão este mês? Algo me preocupa?",
    };
  }

  if (!alert) return null;

  const TONE = {
    critical: {
      border: "border-status-error/50",
      bg: "bg-status-error/8",
      iconBg: "bg-status-error/20",
      iconColor: "text-status-error",
      titleColor: "text-status-error",
    },
    warning: {
      border: "border-status-warning/50",
      bg: "bg-status-warning/8",
      iconBg: "bg-status-warning/20",
      iconColor: "text-status-warning",
      titleColor: "text-status-warning",
    },
  }[alert.tone];

  return (
    <Card
      variant="elevated"
      className={cn(
        "glass-card border p-4 md:p-5 flex items-start gap-3",
        TONE.border, TONE.bg,
      )}
      data-testid="contas-nexo-alert"
    >
      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center shrink-0", TONE.iconBg)}>
        <Sparkles className={cn("w-5 h-5", TONE.iconColor)} aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn("text-caption font-bold mb-0.5", TONE.titleColor)}>
          ✨ {alert.title}
        </p>
        <p className="text-caption text-foreground-muted">{alert.subtitle}</p>
      </div>
      {onTalk && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onTalk(alert.promptHint)}
          className="shrink-0 self-center"
          data-testid="contas-nexo-alert-talk"
        >
          Falar com Nexo
          <ArrowRight className="w-3.5 h-3.5" />
        </Button>
      )}
    </Card>
  );
}
